import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { SavedCV } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { generateCVFilename } from "@/lib/filename";

export const useSavedCVs = ({ onCreditChange }: { onCreditChange?: () => void } = {}) => {
  const { user } = useAuth();
  const [savedCVs, setSavedCVs] = useState<SavedCV[]>([]);
  const [loadingCVs, setLoadingCVs] = useState(false);

  // Load all saved CVs for the current user
  const fetchCVs = useCallback(async () => {
    if (!user) return;
    setLoadingCVs(true);
    const { data, error } = await supabase
      .from("saved_cvs")
      .select("id, user_id, title, created_at, status, pdf_path, company, raw_result")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching CVs:", error);
    } else {
      setSavedCVs(data as SavedCV[]);
    }
    setLoadingCVs(false);
  }, [user]);

  const saveCV = useCallback(async (
    rawResult: string, 
    jobDescription: string, 
    jobTitle: string, 
    company: string, 
    candidateFirstName: string = "", 
    candidateLastName: string = "",
    status: "PREVIEW" | "READY" | "APPLIED" = "READY",
    template: string = "Classic"
  ) => {
    if (!user) return;

    const displayTitle = generateCVFilename(candidateFirstName, candidateLastName, jobTitle, company);

    const { data, error } = await supabase
      .from("saved_cvs")
      .insert({
        user_id: user.id,
        title: displayTitle,
        job_description: jobDescription,
        raw_result: rawResult,
        company: company,
        status: status,
      })
      .select()
      .single();

    if (error) {
      console.error("Error saving CV:", error);
      toast.error("Could not save CV");
      return null;
    } else {
      setSavedCVs((prev) => [data as SavedCV, ...prev]);
      if (status === "READY" || status === "APPLIED") {
        toast.success("CV saved to your account!");
        await supabase.rpc('increment_credits', { user_id: user.id });
        if (onCreditChange) onCreditChange();
      }

      try {
        const { parseCV } = await import("@/lib/cvParser");
        const { generateCVBlob } = await import("@/lib/pdfGenerator");
        const parsed = parseCV(rawResult);
        const blob = await generateCVBlob(parsed, template);
        const path = `${user.id}/${data.id}.pdf`;
        
        const uploadResult = await supabase.storage
          .from("cv-pdfs")
          .upload(path, blob, { upsert: true, contentType: 'application/pdf' });
          
        if (!uploadResult.error) {
          await supabase.from("saved_cvs").update({ pdf_path: path }).eq("id", data.id);
        }
      } catch (e) {
        console.error("Alvaru: PDF auto-generation failed", e);
      }
      
      return data;
    }
  }, [user, onCreditChange]);

  const updateCVStatus = useCallback(async (id: string, status: "PREVIEW" | "READY" | "APPLIED", template: string = "Classic") => {
    if (!user) return false;
    const { error } = await supabase
      .from("saved_cvs")
      .update({ status })
      .eq("id", id)
      .eq("user_id", user.id);

    if (error) {
      console.error("Error updating CV status:", error);
      return false;
    } else {
      if (status === "READY" || status === "APPLIED") {
        await supabase.rpc('increment_credits', { user_id: user.id });
        if (onCreditChange) onCreditChange();
      }

      const cvToUpdate = savedCVs.find(c => c.id === id);
      if (cvToUpdate && cvToUpdate.raw_result) {
        try {
          const { parseCV } = await import("@/lib/cvParser");
          const { generateCVBlob } = await import("@/lib/pdfGenerator");
          const parsed = parseCV(cvToUpdate.raw_result);
          const blob = await generateCVBlob(parsed, template);
          const path = `${user.id}/${id}.pdf`;
          
          const uploadResult = await supabase.storage
            .from("cv-pdfs")
            .upload(path, blob, { upsert: true, contentType: 'application/pdf' });
            
          if (!uploadResult.error) {
            await supabase.from("saved_cvs").update({ pdf_path: path }).eq("id", id);
          }
        } catch (e) {
          console.error("Alvaru: PDF auto-generation failed", e);
        }
      }
      setSavedCVs((prev) => prev.map((cv) => cv.id === id ? { ...cv, status } : cv));
      return true;
    }
  }, [user, onCreditChange, savedCVs]);

  const updateCVTitle = useCallback(async (id: string, title: string) => {
    if (!user) return false;
    const { error } = await supabase
      .from("saved_cvs")
      .update({ title })
      .eq("id", id)
      .eq("user_id", user.id);

    if (error) {
      console.error("Error updating CV title:", error);
      toast.error("Could not update filename");
      return false;
    } else {
      setSavedCVs((prev) => prev.map((cv) => cv.id === id ? { ...cv, title } : cv));
      toast.success("Filename updated");
      return true;
    }
  }, [user]);

  const updateCVContent = useCallback(async (id: string, rawResult: string) => {
    if (!user) return false;
    const { error } = await supabase
      .from("saved_cvs")
      .update({ raw_result: rawResult })
      .eq("id", id)
      .eq("user_id", user.id);

    if (error) {
      console.error("Error updating CV content:", error);
      return false;
    } else {
      setSavedCVs((prev) => prev.map((cv) => cv.id === id ? { ...cv, raw_result: rawResult } : cv));
      return true;
    }
  }, [user]);

  const deleteCV = useCallback(async (id: string, silent = false) => {
    if (!user) return;
    const { error } = await supabase
      .from("saved_cvs")
      .delete()
      .eq("id", id)
      .eq("user_id", user.id);

    if (error) {
      if (!silent) toast.error("Could not delete CV");
    } else {
      setSavedCVs((prev) => prev.filter((cv) => cv.id !== id));
      if (!silent) toast.success("CV deleted");
    }
  }, [user]);

  const fetchFullCV = useCallback(async (id: string) => {
    if (!id || !user?.id) return null;
    const { data, error } = await supabase
      .from("saved_cvs")
      .select("raw_result, job_description, title, company")
      .eq("id", id)
      .eq("user_id", user.id)
      .single();

    if (error) {
      console.error("Error fetching full CV from saved_cvs:", error);
      return null;
    }
    return data;
  }, [user]);

  // Realtime subscription
  useEffect(() => {
    if (!user) return;

    let timeout: any;
    const debouncedRefetch = () => {
      clearTimeout(timeout);
      timeout = setTimeout(fetchCVs, 500);
    };

    const channel = supabase
      .channel('saved-cvs-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'saved_cvs',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const changedFields = payload.new ? Object.keys(payload.new) : [];
          const isSignificantUpdate = payload.eventType === 'UPDATE' && 
            changedFields.some(f => ['title', 'status', 'company', 'pdf_path', 'raw_result'].includes(f));
          
          if (payload.eventType === 'INSERT' || payload.eventType === 'DELETE' || isSignificantUpdate) {
            debouncedRefetch();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchCVs]);

  // Reload whenever the user changes
  useEffect(() => {
    if (user) fetchCVs();
    else setSavedCVs([]);
  }, [user, fetchCVs]);

  return { savedCVs, loadingCVs, saveCV, updateCVStatus, updateCVTitle, updateCVContent, deleteCV, fetchFullCV, refetch: fetchCVs };
};
